/** @module csi/heading/LocalizedStringPopupController */
define([
	"dojo/_base/declare",
	"dojo/_base/lang",
	"dojo/when",
	"csi/_base/storage",
	"csi/_base/underscore",
	"csi/action/ActionView",
	"csi/action/PopupController",
	"csi/def/Action",
	"csi/def/Heading",
	"csi/def/Relationship",
	"csi/heading/String",
	"csi/heading/StringRichController",
	"csi/heading/TextController",
	"csi/locale",
	"csi/reflection",
	"csi/store/_ClientRequestMixin",
	"csi/store/Locales",
	"csi/store/Nodes",
	"csi/table/View",
	"csi/table/Widget",
	"csi/toolbar/MultiSelectSwitchboard",
	"csi/xml"
], function(declare, lang, when, storage, _, ActionView, PopupController, Action, Heading, Relationship, StringRen, StringRichController, TextController, locale, reflection, _ClientRequestMixin, Locales, Nodes, TableView, TableWidget, MultiSelectSwitchboard, xml) {

	var LOCALE_ATTR = "__Locale__";
	var LOCALE_NAME = "Locale";
	var STORAGE_KEY = "LocalizedStringLocales";

	var _LocalesPlugin = declare(MultiSelectSwitchboard, {
		idViewProperty: "locales",
		pageSize: Infinity,
		store: Locales
	});

	var _LocaleRenderer = declare(StringRen, {
		/**
		 * Localize locale name
		 * @param {string} value
		 * @param {string} [fmt]
		 * @return {string}
		 */
		formatValue: function(value, fmt) {
			return locale.resource(value);
		}
	});

	var _LocalizationControllerMixin = declare(null, {
		/**
		 * Apply change to actual node in addition to client node representing locale
		 */
		toWriter: function() {
			this.inherited(arguments);

			var urlClient = this.m_urlNode;
			this.m_urlNode = this.widget.get("urlRoot");
			this._toWriterAttribute(this.heading.m_id, xml.ATTR_STRINGMAP);
			this.m_writer.validateNode(this.m_urlNode, true);
			this.m_urlNode = urlClient;
		}
	});

	var _StringController = declare([TextController, _LocalizationControllerMixin]);
	var _StringRichController = declare([StringRichController, _LocalizationControllerMixin]);

	var _Store = declare([Nodes, _ClientRequestMixin], {
		attributeId: null,
		attributeValue: null,

		/**
		 * Populate data store with client nodes per locale with localized data
		 * @param {QueryResult} [qr]
		 * @return {Object}
		 */
		_resolveData: function(dfd) {
			var attrId = this.attributeId;
			var attrValue = this.attributeValue;
			var type = this.typeNode;

			// this is necessary to support query for locales - otherwise all results will be returned
			this._bSuppressRequest = true;

			when(Locales.query(), function(results) {
				var data = _.map(results, function(item) {
					var node = xml.nodeCreateClientNode(null, type);
					xml.nodeCreateClientAttr(node, LOCALE_ATTR, xml.ATTR_STRING, item[Locales.idProperty]);
					xml.nodeCreateClientAttr(node, attrId, xml.ATTR_STRINGMAP, attrValue);
					return node;
				});

				dfd.resolve(data);
			});
		}
	});

	var _Table = declare(TableWidget, {
		disableCustomViewToolbar: true,
		plugins: ['tableExcel'],
		querySize: 0,
		querySizeFixed: true,

		_actionControllers: {
			ActionString: _StringController,
			ActionStringRich: _StringRichController
		},

		/** @property bResetStore - set to false since store stays constant throughout view */
		bResetStore: false,

		/** @property locales - list of locales to show */
		locales: null,

		/**
		 * @override
		 * Disable sorting in because disableCustomViewToolbar is set to true and the user is unable to edit the sort once they have clicked it
		 * @return {boolean}
		 */
		canSort: function() {
			return false;
		},

		/**
		 * Return query for locales
		 * @param {QueryResult} [qr]
		 * @return {Object}
		 */
		getQueryResultQuery: function(qr) {
			var locales = this.locales;
			var q = {};

			if (locales && locales.length) {
				q[LOCALE_NAME] = locales;
			}

			return q;
		},

		/**
		 * Apply custom data store to query Change Tracking service to main query level
		 * @param {Object} params - parameters for query level
		 * @param {boolean} bRoot - true if creating root query level
		 * @return {QueryLevel}
		 */
		_createQueryLevel: function(params, bRoot) {
			var ql = this.inherited(arguments);
			var self = this;

			if (ql.bMain) {
				ql.createCustomStore = function() {
					var node = self.getViewNode();
					var localizedId = self.getSourceController().getLocalizedId();

					return new _Store({
						attributeId: localizedId,
						attributeValue: node[localizedId],
						cachingStore: self.cacheView,
						querySpecStore: self.get("querySpecStore"),
						typeNode: node.$Type
					});
				};
			}

			return ql;
		},

		/**
		 * Set locale for context based on current result
		 * @param {Context} ctx
		 * @param {QueryResult} qr
		 * @param {DOMNode} td
		 */
		_onFirstCell: function(ctx, qr, td) {
			ctx.locale = ctx.getNodeAttributeValue(LOCALE_ATTR);
			this.inherited(arguments);
		},

		/**
		 * Add locale switchboard and set store for view
		 */
		_resolveView: function() {
			var localeSwitchboard = lang.mixin(new Action(), {
				m_id: "Locales",
				m_postAction: "postActionRefreshData",
				classToolbarPlugin: _LocalesPlugin
			});

			localeSwitchboard.resolve();
			this.plugins.unshift(localeSwitchboard);

			// initialize locales from storage and watch for changes so can store new selections
			this.set("locales", storage.get(STORAGE_KEY) || []);

			this.watch("locales", function(attr, oldValue, newValue) {
				storage.set(STORAGE_KEY, newValue);
			});

			this.inherited(arguments);
		}
	});

	return declare(PopupController, {
		/**
		 * Return view definition to popup
		 * @param {ViewRef} [vr]
		 * @return {ActionView}
		 */
		getActionView: function(vr) {
			var node = this.getNode();
			var defView = lang.mixin(new TableView(), {
				m_id: "__LocalizedString__",
				m_typeView: "Table",
				m_rcRoot: reflection.getNodeClass(node),
				m_headings: [
					lang.mixin(new Heading(), {m_id: LOCALE_ATTR, m_name: LOCALE_NAME, m_right: xml.RIGHT_NONE, classRenderer: _LocaleRenderer}),
					lang.mixin(new Heading(), {m_id: this.getLocalizedId()})
				],
				m_relationships: [
					lang.mixin(new Relationship(), {m_target: node.$Type})
				],
				classController: _Table
			});

			if (!xml.nodeHasAccess(node, this.heading.getRight(), this.heading.m_id)) {
				defView.setReadOnly(true);
			}

			return new ActionView(this, defView, node);
		},

		/**
		 * Return attribute id that is being viewed
		 * @return {string}
		 */
		getLocalizedId: function() {
			return this.heading.m_id;
		},

		/**
		 * Redraw row
		 */
		postProcessSuccess: function() {
			return this.widget.postActionRedrawNode(this);
		}
	});
});
